import React, { useState, useEffect } from "react";
import { Button, Stack, Typography } from "@mui/material";
import { TableContainer, Table, TableCell, TableHead, TableBody, TableRow, Paper } from "@mui/material";
import { Check, Close, Delete, Edit, Email } from "@mui/icons-material";
import UserDeleteModal from "./UserDeleteModal";
import UserAddModal from "./UserAddModal";
import UserEditModal from "./UserEditModal";
import userService from "../../services/userService";
import { accessGuard } from "../../shared/sharedFunctions";
import alertService from "../../services/alertService";

const AdminUsersTable = (props) => {
	
	let [ users, setUsers ] = useState(null);
	let [ deleteUser, setDeleteUser ] = useState(null);
	let [ editUser, setEditUser ] = useState(null);
	let [ showAddUser, setShowAddUser ] = useState(false);
	let [ showAddUserLoading, setShowAddUserLoading ] = useState(false);
	
	// EDIT USER
	const handleEditClick = (user) => {
		setEditUser(user);
	};
	
	const handleEditClose = () => {
		setEditUser(null);
	};
	
	const handleEditSubmit = (user) => {
		userService.update(user)
		.then(() => {
			setUsers(userService.CompanyUsers());
		})
		.catch((err) => {
			console.log(`Error editing user: ${err.message}`);
		})
		.finally(() => {
			setEditUser(null);
		});
	};
	
	const handleAlertsClick = (user, enabled) => {
		if (user.id !== userService.User().id && !accessGuard([1, 2])) {
			alertService.show(`You're account type only permits you to manage your own alert settings.`);
			return;
		}
		
		user.alerts = (enabled)? 1 : 0;
		userService.update(user)
		.then(() => {
			setUsers(userService.CompanyUsers());
		})
		.catch((err) => {
			console.log(`Error editing user alerts: ${err.message}`);
		});
	};
	
	const handleResendClick = async (user) => {
		if (!accessGuard([1])) {
			alertService.show(`Invalid permissions`);
			return;
		}
		
		const link = await userService.resendInvite(user.id);
		
		alertService.show(`Invite sent with the following link: ${link}`);
	};
	
	// DELETE USER
	const handleDeleteClick = (user) => {
		if (user.id === userService.User().id) return;
		
		setDeleteUser(user);
	};
	
	const handleConfirmDelete = (user) => {
		if (user) {
			userService.delete(deleteUser)
			.then(() => {
				setUsers(userService.CompanyUsers());
			})
			.catch((err) => {
				console.log(`Error deleting user: ${err.message}`);
			})
			.finally(() => {
				setDeleteUser(null);
			});
		}
		else {
			setDeleteUser(null);
		}
	};
	
	// ADD USER
	const handleAddClick = () => {
		setShowAddUser(true);
	};
	
	const handleAddClose = () => {
		setShowAddUser(false);
	};
	
	const handleAddSubmit = (user) => {
		setShowAddUserLoading(true);
		userService.add(user)
		.then(() => {
			setUsers(userService.CompanyUsers());
		})
		.catch((err) => {
			console.log(`Error adding user: ${err.message}`);
		})
		.finally(() => {
			setShowAddUser(false);
			setShowAddUserLoading(false);
		});
	};
	
	useEffect(() => {
		setUsers(userService.CompanyUsers());
	}, [ props.company ]);
	
	return (
		<Stack sx={{ width: "80%", margin: "auto" }}>
			<Typography variant="h6" sx={{ margin: "1em" }}>Company Users</Typography>
			<TableContainer component={Paper}>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell sx={{ textAlign: "left" }}>User Name</TableCell>
							<TableCell sx={{ textAlign: "center" }}>Alerts Enabled</TableCell>
							{accessGuard([1])?
								<React.Fragment>
									<TableCell sx={{ textAlign: "center" }}>Resend Invite</TableCell>
								</React.Fragment>
								: <React.Fragment/>
							}
							{accessGuard([1, 2])?
								<React.Fragment>
									<TableCell sx={{ textAlign: "center" }}>Edit</TableCell>
									<TableCell sx={{ textAlign: "center" }}>Delete</TableCell>
								</React.Fragment>
								: <React.Fragment/>
							}
						</TableRow>
					</TableHead>
					<TableBody>
						{users? users.map((user) => (
							<TableRow key={`user_${user.id}`}>
								<TableCell sx={{ textAlign: "left", width: "80%" }}>
									{`${user.firstname} ${user.lastname}`}
								</TableCell>
								<TableCell sx={{ textAlign: "center", width: "10%" }}>
									{(user.alerts === 1)?
										<React.Fragment>
											{(user.id !== userService.User().id && accessGuard([1, 2]))?
												<Button onClick={() => { handleAlertsClick(user, false); }}>
													<Check/>
												</Button>
											:
												<Check/>
											}
										</React.Fragment>									
									:
										<React.Fragment>
											{(user.id !== userService.User().id && accessGuard([1, 2]))?
												<Button onClick={() => { handleAlertsClick(user, true); }}>
													<Close/>
												</Button>
											:
												<Close/>
											}
										</React.Fragment>
									}
								</TableCell>
								{accessGuard([1])?
									<React.Fragment>
										<TableCell sx={{ textAlign: "center", width: "10%" }}>
											<Button onClick={() => {handleResendClick(user)}}>
												<Email/>
											</Button>
										</TableCell>
									</React.Fragment>
									: <React.Fragment/>
								}
								{accessGuard([1, 2])?
									<React.Fragment>
										<TableCell sx={{ textAlign: "center", width: "10%" }}>
											<Button onClick={() => {handleEditClick(user)}}><Edit/></Button>
										</TableCell>
										<TableCell sx={{ textAlign: "center", width: "10%" }}>
											{user.id !== userService.User().id?
												<Button onClick={() => {handleDeleteClick(user)}}><Delete/></Button>
												: <React.Fragment/>
											}
										</TableCell>
									</React.Fragment>
									: <React.Fragment/>
								}
							</TableRow>
						)):<TableRow></TableRow>}
					</TableBody>
				</Table>
			</TableContainer>
			<Button onClick={handleAddClick} sx={{float: "right"}}>+ Add</Button>
			<UserDeleteModal
				user={deleteUser}
				onConfirm={handleConfirmDelete}
			/>
			<UserAddModal
				loading={showAddUserLoading}
				open={showAddUser}
				onClose={handleAddClose}
				onSubmit={handleAddSubmit}
			/>
			<UserEditModal
				user={editUser}
				onClose={handleEditClose}
				onSubmit={handleEditSubmit}
			/>
		</Stack>
	);
}

export default AdminUsersTable;